import { Observable } from "rxjs";
import {
  ACTIONS,
  AdminUpdateRoomStreamReq,
  AdminUpdateRoomStreamRes,
  AdminUpdateRoomStreamsOrderReq,
  AdminUpdateRoomStreamsOrderRes,
  Destination,
  DestinationError,
  DestinationId,
  EVENTS,
  IsAllowedToJoinRoomReq,
  IsAllowedToJoinRoomRes,
  JoinRoomReq,
  JoinRoomRes,
  LeaveRoomReq,
  LeaveRoomRes,
  LicenseLimitExceeded,
  RequestToJoinRoomReq,
  RequestToJoinRoomRes,
  UpdateRoomStreamsOrder
} from "./actions";
import { ErrorData } from "./domain/interface";
import { Socket, SocketConnectData, SocketConnectError } from "./Socket";

export class RoomApi {
  private readonly _socket: Socket;

  constructor(private readonly _config: Config) {
    this._socket = new Socket({
      url: this._config.url
    });
  }

  get connected$(): Observable<boolean> {
    return this._socket.connected$;
  }

  get disconnect$(): Observable<string> {
    return this._socket.disconnect$;
  }

  get connectError$(): Observable<SocketConnectError> {
    return this._socket.connectError$;
  }

  get socket() {
    return this._socket;
  }

  get responseError$() {
    return this._socket.responseError$;
  }

  get state$() {
    return this._socket.state$;
  }

  get lifecycle$() {
    return this._socket.lifecycle$;
  }

  connect(data: SocketConnectData): void {
    this._socket.connect(data);
  }

  disconnect(cause?: string): void {
    this._socket.disconnect(cause);
  }

  isAllowedToJoinRoom(
    data: IsAllowedToJoinRoomReq
  ): Observable<IsAllowedToJoinRoomRes> {
    return this._socket.emit<IsAllowedToJoinRoomRes, IsAllowedToJoinRoomReq>(
      ACTIONS.IS_ALLOWED_TO_JOIN_ROOM,
      data
    );
  }

  requestToJoinRoom(
    data: RequestToJoinRoomReq
  ): Observable<RequestToJoinRoomRes> {
    return this._socket.emit<RequestToJoinRoomRes, RequestToJoinRoomReq>(
      ACTIONS.REQUEST_TO_JOIN_ROOM,
      data
    );
  }

  joinRoom(data: JoinRoomReq): Observable<JoinRoomRes> {
    return this._socket.emit<JoinRoomRes, JoinRoomReq>(ACTIONS.JOIN_ROOM, data);
  }

  leaveRoom(data: LeaveRoomReq): Observable<LeaveRoomRes> {
    return this._socket.emit<LeaveRoomRes, LeaveRoomReq>(
      ACTIONS.LEAVE_ROOM,
      data
    );
  }

  roomStream<R, T>(data: T): Observable<R> {
    return this._socket.emit<R, T>(ACTIONS.ROOM_STREAM, data);
  }

  roomStreamEnd<T>(data: T): Observable<void> {
    return this._socket.emit<void, T>(ACTIONS.ROOM_STREAM_END, data);
  }

  restartStream<T>(data: T): Observable<void> {
    return this._socket.emit<void, T>(ACTIONS.RESTART_STREAM, data);
  }

  updateAudioStream<T>(data: T): Observable<void> {
    return this._socket.emit<void, T>(ACTIONS.UPDATE_AUDIO_STREAM, data);
  }

  /**
   * @deprecated Should not be used.
   */
  adminUpdateRoomStream(
    data: AdminUpdateRoomStreamReq
  ): Observable<AdminUpdateRoomStreamRes> {
    return this._socket.emit<
      AdminUpdateRoomStreamRes,
      AdminUpdateRoomStreamReq
    >(ACTIONS.ADMIN_UPDATE_ROOM_STREAM, data);
  }

  /**
   * @deprecated Should not be used.
   */
  adminUpdateRoomStreamsOrder(
    data: AdminUpdateRoomStreamsOrderReq
  ): Observable<AdminUpdateRoomStreamsOrderRes> {
    return this._socket.emit<
      AdminUpdateRoomStreamsOrderRes,
      AdminUpdateRoomStreamsOrderReq
    >(ACTIONS.ADMIN_UPDATE_ROOM_STREAMS_ORDER, data);
  }

  listRoomStreams<R, T>(data: T): Observable<R> {
    return this._socket.emit<R, T>(ACTIONS.LIST_ROOM_STREAMS, data);
  }

  listenErrorEvent(): Observable<ErrorData> {
    return this._socket.listen<ErrorData>(EVENTS.ERROR);
  }

  listenRestartStreamEvent<T>() {
    return this._socket.listen<T>(EVENTS.RESTART_STREAM);
  }

  listenUpdateAudioStreamEvent<T>() {
    return this._socket.listen<T>(EVENTS.UPDATE_AUDIO_STREAM);
  }

  /**
   * @deprecated Should not be used.
   */
  listenUpdateRoomStreamsOrderEvent() {
    return this._socket.listen<UpdateRoomStreamsOrder>(
      EVENTS.UPDATE_ROOM_STREAMS_ORDER
    );
  }

  listenAddDestination() {
    return this._socket.listen<Destination>(EVENTS.ADD_DESTINATION);
  }

  listenUpdateDestination() {
    return this._socket.listen<Destination>(EVENTS.UPDATE_DESTINATION);
  }

  listenDeleteDestination() {
    return this._socket.listen<DestinationId>(EVENTS.DELETE_DESTINATION);
  }

  listenErrorDestination() {
    return this._socket.listen<DestinationError>(EVENTS.ERROR_DESTINATION);
  }

  listenLicenseLimitExceeded() {
    return this._socket.listen<LicenseLimitExceeded>(
      EVENTS.LICENSE_LIMIT_EXCEEDED
    );
  }

  listenRoomBrandBgDeferredError(): Observable<ErrorData> {
    return this._socket.listen<ErrorData>(EVENTS.ROOM_BRAND_BG_DEFERRED_ERROR);
  }

  listenRoomBrandBgSet<T>() {
    return this._socket.listen<T>(EVENTS.ROOM_BRAND_BG_SET);
  }

  listenRoomBrandBgDelete<T>() {
    return this._socket.listen<T>(EVENTS.ROOM_BRAND_BG_DELETE);
  }

  listenRoomBrandCaptionLowerThirdDeferredError(): Observable<ErrorData> {
    return this._socket.listen<ErrorData>(
      EVENTS.ROOM_BRAND_CAPTION_LOWER_THIRD_DEFERRED_ERROR
    );
  }

  listenRoomBrandCaptionLowerThirdDelete<T>() {
    return this._socket.listen<T>(EVENTS.ROOM_BRAND_CAPTION_LOWER_THIRD_DELETE);
  }

  listenRoomBrandCaptionLowerThirdSet<T>() {
    return this._socket.listen<T>(EVENTS.ROOM_BRAND_CAPTION_LOWER_THIRD_SET);
  }

  listenRoomBrandLogoDeferredError(): Observable<ErrorData> {
    return this._socket.listen<ErrorData>(
      EVENTS.ROOM_BRAND_LOGO_DEFERRED_ERROR
    );
  }

  listenRoomBrandLogoSet<T>() {
    return this._socket.listen<T>(EVENTS.ROOM_BRAND_LOGO_SET);
  }

  listenRoomBrandLogoDelete<T>() {
    return this._socket.listen<T>(EVENTS.ROOM_BRAND_LOGO_DELETE);
  }

  listenRoomBrandOverlayDeferredError(): Observable<ErrorData> {
    return this._socket.listen<ErrorData>(
      EVENTS.ROOM_BRAND_OVERLAY_DEFERRED_ERROR
    );
  }

  listenRoomBrandOverlaySet<T>() {
    return this._socket.listen<T>(EVENTS.ROOM_BRAND_OVERLAY_SET);
  }

  listenRoomBrandOverlayDelete<T>() {
    return this._socket.listen<T>(EVENTS.ROOM_BRAND_OVERLAY_DELETE);
  }

  /**
   * @deprecated
   * @todo Migrate to listenRoomLiveStreamStatusUpdate.
   */
  listenRoomMixerError<T>() {
    return this._socket.listen<T>(EVENTS.ROOM_MIXER_ERROR);
  }

  /**
   * @deprecated
   * @todo Migrate to listenRoomLiveStreamStatusUpdate.
   */
  listenRoomMixerStart<T>() {
    return this._socket.listen<T>(EVENTS.ROOM_MIXER_START);
  }

  /**
   * @deprecated
   * @todo Migrate to listenRoomLiveStreamStatusUpdate.
   */
  listenRoomMixerStarted<T>() {
    return this._socket.listen<T>(EVENTS.ROOM_MIXER_STARTED);
  }

  /**
   * @deprecated
   * @todo Migrate to listenRoomLiveStreamStatusUpdate.
   */
  listenRoomMixerToRotate<T>() {
    return this._socket.listen<T>(EVENTS.ROOM_MIXER_TO_ROTATE);
  }

  listenRoomLiveStreamStatusUpdate<T>() {
    return this._socket.listen<T>(EVENTS.ROOM_LIVE_STREAM_STATUS_UPDATE);
  }

  listenRoomStageLayoutDeferredError(): Observable<ErrorData> {
    return this._socket.listen<ErrorData>(
      EVENTS.ROOM_STAGE_LAYOUT_DEFERRED_ERROR
    );
  }

  listenRoomStageLayoutStreamsSet<T>() {
    return this._socket.listen<T>(EVENTS.ROOM_STAGE_LAYOUT_STREAMS_SET);
  }

  listenRoomStreamEvent<T>(): Observable<T> {
    return this._socket.listen<T>(EVENTS.ROOM_STREAM);
  }

  listenRoomStreamEndEvent<T>() {
    return this._socket.listen<T>(EVENTS.ROOM_STREAM_END);
  }

  listenRoomStreamUpdateEvent<T>(): Observable<T> {
    return this._socket.listen<T>(EVENTS.ROOM_STREAM_UPDATE);
  }
}

interface Config {
  url: string;
}
